<template>
  <k-modal v-if="isReady" id="add-new-otj-modal" :show="showModal"
    @close="closeModal"
    ref="otjModal"
  >
    <template #header>
      <h3>{{ modalTitle }}</h3>
    </template>
    <template #body>
      <div class="modal-body">
        <k-disclaimer v-if="complianceMode || activityInFuture" type="warning">
          <p v-if="complianceMode">
            You are directly editing a learner's O.T.J. entry and your changes will be saved to their record.
            This functionality should only be used to fix errors or issues in their O.T.J. submissions that
            cannot be resolved by the usual review, approval and referral workflow between the learner and coach.
          </p>
          <p v-if="activityInFuture" class="future-date-disclaimer">
            You are trying to submit an O.T.J. for an activity that starts in the future.
            To submit for review, please set the activity to start today or in the past, or save this as a draft for future submission.
          </p>
        </k-disclaimer>
        <form class="form-date-picker">
          <fieldset class="form-group form-entry" :class="{ 'has-error': showErrors && !newOtjActivityDate, 'has-warning': activityInFuture }">
            <k-date-picker
              v-model="newOtjActivityDate"
              label="Date"
              :popover="{ visibility: 'click' }"
              :modelConfig="{ timeAdjust: '00:00:00' }"
              :required="true"
            />
            <small v-if="showErrors && !newOtjActivityDate" class="field-error">This field is required</small>
          </fieldset>
          <fieldset class="form-group form-entry" :class="{ 'has-error': showErrors && (!newOtjStartTime || !validTimeRange) }">
            <k-date-picker
              v-model="newOtjStartTime"
              label="Start Time"
              type="time"
              mode="time"
              input-placeholder="Select time..."
              :time-rules="{ minutes: { interval: 5 } }"
              :popover="{ visibility: 'click' }"
              :modelConfig="{ timeAdjust: '00:00:00' }"
              :required="true"
            />
            <small v-if="showErrors && !newOtjStartTime " class="field-error">This field is required</small>
          </fieldset>
          <fieldset class="form-group form-entry"
            :class="{ 'has-error': showErrors && (!newOtjEndTime || !validTimeRange || showPastTimesError) }"
          >
            <k-date-picker
              v-model="newOtjEndTime"
              label="End Time"
              type="time"
              mode="time"
              input-placeholder="Select time..."
              :time-rules="{ minutes: { interval: 5 } }"
              :popover="{ visibility: 'click' }"
              :modelConfig="{ timeAdjust: '00:00:00' }"
              :prohibit-future-dates="true"
              :required="true"
            />
            <small v-if="showErrors && !newOtjEndTime" class="field-error">This field is required</small>
            <small v-if="showErrors && !validTimeRange" class="field-error">End time must be after start time</small>
            <small v-if="showErrors && showPastTimesError" class="field-error">Both times must be in the past</small>
          </fieldset>
          <fieldset class="form-group form-entry">
            <div class="time-spent-container">
              <label for="time-spent">Time Spent</label>
              <input class="form-control" id="time-spent" type="text" v-model="formattedTimeSpent" readonly />
            </div>
          </fieldset>
        </form>
        <div class="learning-activity-container">
          <div class="dropdown-container form-required" :class="{ 'has-error': showErrors && !newOtjLearningActivityType }">
            <label>Learning Activity Type</label>
            <k-dropdown
              v-model="newOtjLearningActivityType"
              :options="learningActivityTypes"
              :show-search="false"
              placeholder="Select Learning Activity Type"
            />
            <small v-if="showErrors && !newOtjLearningActivityType" class="field-error">This field is required</small>
          </div>
          <div v-if="!complianceMode" class="expandable-container">
            <div class="expandable-header">
              <button class="btn btn-link" aria-describedby="toggle-description" @click="toggleExpandableContent">
                <span>Example of OTJ Learning Activity Type<i class="fas" :class="`fa-angle-${showExpandableContent ? 'up' : 'down'}`" aria-hidden="true"></i></span>
              </button>
              <span class="sr-only" id="toggle-description">{{ showExpandableContent ? 'Hide' : 'Show' }} example</span>
            </div>
            <div class="expandable-content" v-if="showExpandableContent">
              <ul>
                <li v-for="learningActivityType in learningActivityTypes" :key="learningActivityType.id">
                  <strong>{{ learningActivityType.name }}</strong>
                  <p>{{ learningActivityType.description }}</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <fieldset class="form-group comment-container form-required" :class="{ 'has-error': showErrors && !validDescription }">
          <label for="otj-learner-comment">
            What have you learned that you didn't know before undertaking this activity?
          </label>
          <textarea
            id="otj-learner-comment"
            class="ph-no-capture"
            placeholder="Type your comment here..."
            v-model="newOtjLearningDescription"
            rows="3"
            :maxlength="descMaxLength"/>
          <k-character-count :value-length="newOtjLearningDescription.length" :max-length="descMaxLength"></k-character-count>
          <small v-if="showErrors && !validDescription" class="field-error">This field is required</small>
          <div v-if="isOtjAiEnabled" class="feedback-button-container">
            <button
              class="btn btn-outlined btn-sm"
              @click="getDescriptionFeedback"
              :disabled="!validDescription || loadingFeedback"
              :title="validDescription ? 'Get feedback on your learning description' : 'Please enter what you learned first'"
            >
              <img v-if="!loadingFeedback" :src="chatbotImage" class="chatbot-icon-small" alt="KATE bot avatar"/>
              <i v-if="loadingFeedback" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
              {{ loadingFeedback ? 'Getting Feedback...' : 'Ask KATE for Feedback' }}
            </button>
          </div>
          <!-- OTJ Description Feedback -->
          <div v-if="isOtjAiEnabled && showDescriptionFeedback" class="otj-feedback-section">
            <div class="feedback-header">
              <img :src="chatbotImage" class="chatbot-icon" alt="KATE bot avatar"/>
              <div class="title-section">
                <h4 class="feedback-title">KATE's Feedback</h4>
                <k-ai-generated-disclosure />
              </div>
            </div>
            <div class="feedback-container">
              <div v-if="loadingFeedback" class="feedback-loading">
                <i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Getting feedback...
              </div>
              <div v-else-if="feedbackError" class="feedback-error">
                <i class="fas fa-exclamation-circle" aria-hidden="true"></i> {{ feedbackError }}
              </div>
              <div v-else-if="descriptionFeedback" class="feedback-content">
                <p>{{ descriptionFeedback }}</p>
              </div>
            </div>
          </div>
        </fieldset>
        <div class="competencies-container">
          <otj-competency-selector :class="{ 'has-competencies-error': showErrors && competenciesRequiredError }"
            ref="competencySelector"
            :programme-id="programmeId"
            :learning-description="newOtjLearningDescription"
            :learning-activity-type="getLearningActivityType()"
            :otj-data="otjData"
            :show-modal="showModal"
            @update:selectedCompetencies="handleSelectedCompetenciesUpdate"
          />
          <small v-if="showErrors && competenciesRequiredError" class="field-error">Please select at least one competency</small>
        </div>
        <div v-if="!complianceMode" class="confirmation-container form-required" :class="{ 'has-error': showErrors && !otjConfirmation }">
          <label>
            <input type="checkbox" v-model="otjConfirmation"/>I confirm that all off-the-job training took place during my paid working hours and was directly relevant to my apprenticeship,
            providing new knowledge, skills, or behaviours needed for achievement.
          </label>
        </div>
        <div v-if="complianceMode" class="status-container form-required">
          <label>Status</label>
          <div style="display: flex;">
            <template v-if="editStatus">
              <ul class="status-options">
                <li v-for="status in statusOptions">
                  <button @click="selectStatus(status)">
                    <span class="status-badge" :class="`${status}`">{{ status }}</span>
                  </button>
                </li>
              </ul>
            </template>
            <template v-else>
              <span class="status-badge" :class="`${newOtjStatus}`">
                {{ newOtjStatus }}
              </span>
              <k-icon-button icon="fas fa-pencil-alt" classes="btn btn-primary" aria-label="Edit status" @click="editStatus = true"/>
            </template>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="modal-footer-left">
        <button class="btn btn-outlined" @click="closeModal">Cancel</button>
      </div>
      <div class="modal-footer-right">
        <template v-if="complianceMode">
          <button v-if="complianceMode" class="btn btn-danger" @click="confirmOtjDeletion" :disabled="saving">
            Delete
          </button>
          <button class="btn btn-success" @click="handleSubmit(newOtjStatus)">
            <i v-if="saving" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
            Update
          </button>
        </template>
        <template v-else>
          <button class="btn btn-default" @click="handleSubmit('draft')">
            <i v-if="saving" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
            Save as Draft
          </button>
          <button class="btn btn-success" @click="handleSubmit('pending')">
            <i v-if="saving" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
            Submit for Review
          </button>
        </template>
      </div>
    </template>
  </k-modal>
</template>

<style>
#add-new-otj-modal .modal-footer {
  display: flex;
  justify-content: space-between;
}

#add-new-otj-modal .modal-container {
  width: 80vw;
}

#add-new-otj-modal .form-control[readonly] {
  background-color: var(--input-background);
  border: var(--input-border);
}

.status-container .k-icon-button {
  margin-left: 10px;
}

#add-new-otj-modal .has-competencies-error table {
  outline: 1px solid var(--kate-type-danger);
}
</style>

<style scoped>
.modal-body {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.form-date-picker {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 15px;
}

.learning-activity-container {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.comment-container,
.time-spent-container {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
}

.comment-container > label {
  width: 100%;
}

.feedback-button-container {
  margin-top: 10px;
  display: flex;
  justify-content: flex-start;
}

.otj-feedback-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--kate-border-color);
  width: 100%;
  box-sizing: border-box;
}

.feedback-header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 15px;
}

.chatbot-icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.title-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.feedback-title {
  margin: 0;
  color: var(--kate-type-primary);
}

.feedback-container {
  min-height: 100px;
  overflow-y: auto;
  background: var(--kate-chat);
  border: var(--kate-chat-border);
  border-radius: 12px;
  padding: 15px;
  width: 100%;
  box-sizing: border-box;
}

.feedback-loading,
.feedback-error {
  text-align: center;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  min-width: 100%;
}

.feedback-error {
  color: var(--kate-type-danger);
}

.feedback-content {
  color: var(--kate-type-light);
  line-height: 1.6;
}

.feedback-content p {
  margin: 0 0 10px;
}

.suggested-competencies-text {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid var(--kate-border-color);
  color: var(--kate-type-primary);
}

.chatbot-icon-small {
  width: 20px;
  height: 20px;
}

.status-container {
  display: flex;
  flex-direction: column;
}

ul.status-options {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: row;
  gap: 5px;
}

ul.status-options li > button {
  background-color: transparent;
}

.comment-container textarea {
  width: 100%;
  min-height: 80px;
  padding: 10px;
  border: var(--input-border-alt);
  border-radius: 4px;
  resize: vertical;
}

.btn-link:focus {
  color: var(--kate-type-link);
  outline-offset: 2px;
}

.btn-link i {
  margin-left: 5px;
}

.expandable-content {
  background-color: var(--kate-background-alt);
  padding: 10px;
  margin-top: 10px;
  border-radius: 8px;
  box-shadow: 0 0 1px 2px var(--kate-background-alpha);
}

.confirmation-container input {
  margin-right: 5px;
}

.confirmation-container label {
  cursor: pointer;
}

.confirmation-container label:hover {
  text-decoration: underline;
}

i.fas.fa-spinner.fa-spin {
  width: fit-content;
}

#time-spent[readonly]:focus {
  outline: none;
  box-shadow: none;
}

#time-spent[readonly] {
  text-align: center;
}

.field-error {
  color: var(--kate-type-danger);
  margin-top: 6px;
  font-size: 0.9rem;
}

.has-error :deep(.form-control),
.has-error :deep(textarea),
.has-error :deep(.date-picker-input) {
  border: 1px solid var(--kate-type-danger) !important;
  box-shadow: 0 0 0 2px var(--kate-background-alpha);
}

.has-warning :deep(.form-control),
.has-warning :deep(textarea),
.has-warning :deep(.date-picker-input) {
  border: 1px solid var(--kate-type-warning) !important;
}

.has-error label {
  color: var(--kate-type-danger);
}

#otj-learner-comment {
  margin-bottom: 10px;
}

</style>

<script>
import useOtjModalStore from '@stores/otj-modal-store';
import KModal from '@base-components/k-modal.vue';
import KDatePicker from '@base-components/k-date-picker.vue';
import KDropdown from '@base-components/k-dropdown.vue';
import KIconButton from '@base-components/buttons/k-icon-button.vue';
import { KCharacterCount } from '@kate/components';
import KDisclaimer from '@base-components/k-disclaimer.vue';
import TimeMixin from '@mixins/time-mixins';
import { LEARNING_ACTIVITY_TYPES } from '@constants';
import KAiGeneratedDisclosure from '@base-components/ai/k-ai-generated-disclosure.vue';
import { importImage } from '@utils/dynamic-image-importer';
import OtjCompetencySelector from './otj-competency-selector.vue';

const DESC_MAX_LENGTH = 2048;

export default {
  components: {
    KModal,
    KDatePicker,
    KDropdown,
    KIconButton,
    KCharacterCount,
    OtjCompetencySelector,
    KDisclaimer,
    KAiGeneratedDisclosure,
  },

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    programmeId: {
      type: Number,
      required: true,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    otjData: {
      type: Object,
      default: undefined,
    },
    // Compliance mode props
    complianceMode: {
      type: Boolean,
      default: false,
    },
    learnerId: {
      type: Number,
      required: false,
    },
    programmeMemberId: {
      type: Number,
      required: false,
    },
  },

  mixins: [TimeMixin],

  data() {
    return {
      otjModalStore: useOtjModalStore(),
      newOtjLearningDescription: '',
      newOtjActivityDate: null,
      newOtjStartTime: null,
      newOtjEndTime: null,
      newOtjTimeSpent: 0,
      newOtjLearningActivityType: undefined,
      newOtjStatus: this.otjData?.status,
      learningActivityTypes: LEARNING_ACTIVITY_TYPES,
      showExpandableContent: false,
      draftCompetencies: [],
      otjConfirmation: false,
      isReady: true,
      saving: false,
      descMaxLength: DESC_MAX_LENGTH,
      editStatus: false,
      loadingFeedback: false,
      showDescriptionFeedback: false,
      descriptionFeedback: '',
      suggestedCompetenciesText: '',
      feedbackError: null,
      selectedCompetencies: [],
      showErrors: false,
      submitContext: null,
    };
  },

  beforeMount() {
    this.resetActivityDateAndTimes();
  },
  watch: {
    otjData: {
      handler(newVal) {
        if (newVal) {
          this.newOtjActivityDate = newVal.start_time ? this.getDate(newVal.start_time) : this.getTodayMidday();
          this.newOtjStartTime = newVal.start_time ? this.getDate(newVal.start_time) : this.getNowWithoutSeconds();
          this.newOtjEndTime = newVal.end_time ? this.getDate(newVal.end_time) : this.getNowWithoutSeconds();
          this.newOtjTimeSpent = newVal.time_spent || 0;
          // Map "rejected" to "referred" for display purposes
          this.newOtjStatus = newVal.status === 'rejected' ? 'referred' : newVal.status;

          // Set learning activity type
          if (newVal.category) {
            const activityType = this.learningActivityTypes.find(
              type => type.name === newVal.category,
            );
            this.newOtjLearningActivityType = activityType ? activityType.id : undefined;
          } else {
            this.newOtjLearningActivityType = undefined;
          }

          this.newOtjLearningDescription = newVal.learning_description || '';
          this.otjConfirmation = newVal.declared_in_working_hours || false;
          this.draftCompetencies = newVal.competencies || [];
        } else {
          // Reset form when no OTJ data is provided
          this.clearFields();
        }
      },
      immediate: true,
    },
  },

  computed: {
    modalTitle() {
      if (!this.isEditing) {
        return 'Add New O.T.J.';
      }
      if (this.otjData?.status.toLowerCase() === 'rejected') {
        return 'Edit Referred O.T.J.';
      } if (this.otjData?.status.toLowerCase() === 'draft') {
        return 'Edit Draft O.T.J.';
      }
      return 'Edit O.T.J.';
    },

    activityInFuture() {
      if (!this.newOtjStartTime || !this.newOtjActivityDate) {
        return false;
      }
      return this.overrideDateComponent(this.newOtjStartTime, this.newOtjActivityDate) > new Date();
    },

    showPastTimesError() {
      return this.submitContext === 'review' && !this.activityInFuture && this.timesInFuture;
    },

    validSubmitForReview() {
      return Boolean(this.otjConfirmation && this.validDescription && this.newOtjLearningActivityType && this.newOtjActivityDate && !this.saving && !this.activityInFuture && this.validTimeRange && !this.timesInFuture && this.selectedCompetencies.length > 0);
    },

    validSaveAsDraft() {
      return Boolean(!this.saving && this.validTimeRange && this.newOtjActivityDate);
    },

    validDescription() {
      return this.newOtjLearningDescription.trim() !== '';
    },

    validTimeRange() {
      // Pure time ordering check (no date component involved).
      // This avoids false negatives when the time pickers carry a different internal date.
      if (!this.newOtjStartTime || !this.newOtjEndTime) {
        return false;
      }
      const startMinutes = this.newOtjStartTime.getHours() * 60 + this.newOtjStartTime.getMinutes();
      const endMinutes = this.newOtjEndTime.getHours() * 60 + this.newOtjEndTime.getMinutes();

      // Check that end time is at or after start time.
      return endMinutes >= startMinutes;
    },

    timesInFuture() {
      if (!this.newOtjStartTime || !this.newOtjEndTime || !this.newOtjActivityDate) {
        return false;
      }
      // Check that neither start time nor end time are in the future
      const now = new Date();
      const combinedStartDateTime = this.overrideDateComponent(this.newOtjStartTime, this.newOtjActivityDate);
      const combinedEndDateTime = this.overrideDateComponent(this.newOtjEndTime, this.newOtjActivityDate);

      return combinedStartDateTime > now || combinedEndDateTime > now;
    },

    formattedTimeSpent() {
      // For new OTJs or when start/end times have changed, calculate from start and end times
      if (!this.newOtjStartTime || !this.newOtjEndTime) {
        return '';
      }

      const diffHours = this.newOtjEndTime.getHours() - this.newOtjStartTime.getHours();
      const diffMinutes = 60 * diffHours + this.newOtjEndTime.getMinutes() - this.newOtjStartTime.getMinutes();

      if (diffMinutes < 0) {
        return 'Invalid time range';
      }

      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;
      return `${hours}h ${minutes}m`;
    },

    statusOptions() {
      return ['pending', 'approved', 'referred'];
    },

    updateOtjEntryUrl() {
      return `/api/curriculum/${this.complianceMode ? 'admin/' : ''}programmes/${this.programmeId}/otj-entries/${this.otjData.id}`;
    },

    chatbotImage() {
      return importImage('icons/kate-bot-chat-colours.svg');
    },

    isOtjAiEnabled() {
      return this.$featureFlags && this.$featureFlags.isEnabled('otj-ai-enhancements');
    },
    competenciesRequiredError() {
      return this.selectedCompetencies.length === 0;
    },
  },

  methods: {
    handleSubmit(status) {
      this.submitContext = status === 'draft' ? 'draft' : 'review';
      this.showErrors = true;
      if (!this.validSaveAsDraft && status === 'draft') {
        return;
      }
      if (!this.validSubmitForReview && status === 'pending') {
        return;
      }
      this.saveChanges(status);
    },

    getTodayMidday() {
      const today = new Date();
      today.setHours(12, 0, 0, 0);
      return today;
    },

    getNowWithoutSeconds() {
      const now = new Date();
      now.setSeconds(0, 0);
      return now;
    },

    removeSeconds(date) {
      date.setSeconds(0, 0);
      return date;
    },

    getOtjEntryPayload(status) {
      // Map "referred" to "rejected" for backend compatibility
      const backendStatus = status === 'referred' ? 'rejected' : status;

      const payload = {
        category: this.getLearningActivityType()?.name || '',
        learning_description: this.newOtjLearningDescription || '',
        declared_in_working_hours: this.otjConfirmation,
        programme_competency_ids: this.selectedCompetencies.map(competency => competency.id),
        status: backendStatus,
        start_time: this.formatDate(this.removeSeconds(this.overrideDateComponent(this.newOtjStartTime, this.newOtjActivityDate))),
        end_time: this.formatDate(this.removeSeconds(this.overrideDateComponent(this.newOtjEndTime, this.newOtjActivityDate))),
      };
      if (this.complianceMode) {
        payload.user_id = this.learnerId;
        payload.programme_member_id = this.programmeMemberId;
      }
      return payload;
    },

    toggleExpandableContent() {
      this.showExpandableContent = !this.showExpandableContent;
    },

    handleSelectedCompetenciesUpdate(selectedCompetencies) {
      this.selectedCompetencies = selectedCompetencies;
    },

    getLearningActivityType() {
      return this.learningActivityTypes.find(type => type.id === this.newOtjLearningActivityType);
    },

    selectStatus(status) {
      this.newOtjStatus = status;
      this.editStatus = false;
    },

    createOtjEntry(status) {
      this.saving = true;
      const payload = this.getOtjEntryPayload(status);
      this.$logger.info('Creating OTJ entry', { status }, true);
      const toastNoun = status === 'draft' ? 'draft O.T.J.' : 'O.T.J.';
      this.$http.post(`/api/curriculum/programmes/${this.programmeId}/otj-entries`, payload)
        .then(() => {
          this.$ktoast.success(`${status === 'draft' ? 'Saved' : 'Submitted'} ${toastNoun}`);
          this.$logger.info('OTJ entry created', { status }, true);
          this.$emit('update');
          this.clearFields();
          this.closeModal();
        }).catch(err => {
          this.$logger.autowarn('Error creating OTJ entry', { status }, err);
          if (err.response.status === 400) {
            this.$ktoast.showError(err);
          } else {
            this.$ktoast.warning(`Error ${status === 'draft' ? 'saving' : 'submitting'} ${toastNoun}`);
          }
        }).then(() => {
          this.saving = false;
        });
    },

    updateOtjEntry(status) {
      this.saving = true;
      const payload = this.getOtjEntryPayload(status);
      const logPayload = { otjEntryId: this.otjData.id, status, complianceMode: this.complianceMode };
      const toastNoun = status === 'draft' ? 'draft O.T.J.' : 'O.T.J.';
      this.$logger.info('Updating OTJ entry', logPayload, true);
      this.$http.put(this.updateOtjEntryUrl, payload)
        .then(() => {
          let toastVerb = status === 'draft' ? 'Saved' : 'Submitted';
          if (this.complianceMode) {
            toastVerb = 'Updated';
          }
          this.$ktoast.success(`${toastVerb} ${toastNoun}`);
          this.$logger.info('OTJ entry updated', logPayload, true);
          this.$emit('update');
          this.closeModal();
          this.clearFields();
        }).catch(err => {
          this.$logger.autowarn('Error updating OTJ entry', logPayload, err);
          if (err.response.status === 400) {
            this.$ktoast.showError(err);
          } else {
            let toastVerb = status === 'draft' ? 'saving' : 'submitting';
            if (this.complianceMode) {
              toastVerb = 'updating';
            }
            this.$ktoast.warning(`Error ${toastVerb} ${toastNoun}`);
          }
        }).then(() => {
          this.saving = false;
        });
    },

    submitRejectedOtj(status) {
      if (!this.otjData || !this.programmeId) {
        return;
      }
      this.saving = true;
      const payload = this.getOtjEntryPayload(status);
      this.$http.post(`/api/curriculum/programmes/${this.programmeId}/otj-entries/${this.otjData.id}/re-submission`, payload)
        .then(() => {
          this.$logger.info('OTJ re-submitted', { otjId: this.otjData.id });
          this.$ktoast.success('OTJ re-submitted');
          this.$emit('update');
          this.closeModal();
        })
        .catch(err => {
          this.$logger.autowarn('Error submitting rejected OTJ', { otjId: this.otjData.id }, err);
          this.$ktoast.showError(err, true);
        }).then(() => {
          this.saving = false;
        });
    },

    deleteOtjEntry() {
      this.saving = true;
      this.$logger.info('Privileged user deleting OTJ for learner from dashboard', { otjEntryId: this.otjData.id });
      this.$http.delete(`/api/curriculum/admin/programmes/${this.programmeId}/otj-entries/${this.otjData.id}/${this.learnerId}`)
        .then(() => {
          this.$logger.info('Learner OTJ entry deleted by privileged user', { otjEntryId: this.otjData.id });
          this.$ktoast.success('Entry deleted');
          this.$emit('update');
          this.closeModal();
        }).catch(err => {
          this.$logger.autowarn('Error deleting learner OTJ entry', undefined, err);
          this.$ktoast.showError(err);
        }).then(() => {
          this.saving = false;
        });
    },

    confirmOtjDeletion() {
      this.$ktoast.confirmToast('You are about to delete this OTJ entry. This cannot be undone.', 'warning', () => {
        this.deleteOtjEntry();
      });
    },

    saveChanges(status) {
      // In compliance mode, we're only ever editing an existing OTJ entry
      if (this.complianceMode) {
        this.updateOtjEntry(status);
        return;
      }

      const previousStatus = this.otjData?.status.toLowerCase();
      if (this.isEditing && previousStatus === 'rejected') {
        // If editing a rejected OTJ, create a new one instead of editing the original
        this.submitRejectedOtj(status);
      } else if (this.isEditing) {
        this.updateOtjEntry(status);
      } else {
        this.createOtjEntry(status);
      }
    },

    resetActivityDateAndTimes() {
      // Activity date is set to the day
      this.newOtjActivityDate = this.getTodayMidday();
      // Start time and end time are set to the minute
      this.newOtjStartTime = this.getNowWithoutSeconds();
      this.newOtjEndTime = this.getNowWithoutSeconds();
    },

    clearFields() {
      this.resetActivityDateAndTimes();
      this.newOtjTimeSpent = 0;
      this.newOtjLearningActivityType = undefined;
      this.newOtjLearningDescription = '';
      this.newOtjStatus = undefined;
      this.otjConfirmation = false;
      this.showDescriptionFeedback = false;
      this.descriptionFeedback = '';
      this.suggestedCompetenciesText = '';
      this.feedbackError = null;
    },

    getDescriptionFeedback() {
      if (!this.validDescription) {
        return;
      }

      this.loadingFeedback = true;
      this.showDescriptionFeedback = true;
      this.feedbackError = null;
      this.$logger.info('Requesting OTJ description feedback', { programmeId: this.programmeId }, true);

      // Get competencies from the child component
      const competencySelector = this.$refs.competencySelector;
      const competenciesList = competencySelector && competencySelector.competencies
        ? competencySelector.competencies.map(comp => ({
          id: comp.id,
          label: comp.label,
          category: comp.category,
          description: comp.description,
        }))
        : [];

      // Get learning activity type name
      const learningActivityType = this.getLearningActivityType()?.name || null;

      const payload = {
        learning_description: this.newOtjLearningDescription.trim(),
        learning_activity_type: learningActivityType,
        competencies: competenciesList,
      };

      this.$http.post(`/api/chatbot/otj/description-feedback/${this.programmeId}`, payload)
        .then(response => {
          // Extract just the feedback paragraph, removing any suggested competencies text
          let feedback = response.data.feedback || '';
          // Remove the suggested competencies line if it appears in the feedback
          feedback = feedback.replace(/\n?Suggested Competencies:.*$/i, '').trim();
          this.descriptionFeedback = feedback;
          this.suggestedCompetenciesText = response.data.suggested_competencies || '';
          this.$logger.info('OTJ description feedback received', { programmeId: this.programmeId }, true);
        })
        .catch(err => {
          this.$logger.error('Error getting description feedback', { programmeId: this.programmeId }, err);
          this.feedbackError = 'Failed to get feedback. Please try again.';
        })
        .then(() => {
          this.loadingFeedback = false;
        });
    },

    closeModal() {
      this.$emit('close');
      this.otjModalStore.closeOtjModal();
    },
  },
};
</script>
